import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { buyerSelector, AuthDisplay, LOGIN } from '@src/features/customers';
import { useWidgetId } from '@src/core/IdProvider';
import { classNamespaceFactory } from '@src/common';
import { callHostFunction } from '@src/common/hostCallbacks';
import {
  CashbackGrowthOrchestrator,
  loadReturnOrder,
  currentOrderSelector,
  hasPendingSaleRequiredFormInputsSelector,
  loadPendingOrders,
  setupCheckout,
  resolveOrderCheckoutOutcome,
  OrderCheckoutOutcome,
  getOrderAnalyticsData,
  toSellerTemplateOrder,
  CASHBACK_GROWTH_FEATURE_FLAG,
} from '@src/features/orders';
import { Order, Buyer, SaleChange } from '@src/core/buyerApiClient';
import { CartDisplay } from '@src/features/cart';
import StepNavigationIcon from '@src/components/Product/StepNavigationIcon';
import { useAppSelector, useAppDispatch, useWidgetParam } from '@src/core/hooks';
import { setStepCheckout, stepCheckoutSelector, checkoutStepState } from '@src/stores/checkout';
import { loadReturnSale } from '@src/stores/sale';
import {
  loadSaleChangeByIdAsCurrent,
  resetSaleChange,
  setCurrentSaleChange,
  setShowAppliedFeedbackForSaleChange,
} from '@src/stores/saleChange';

import {
  AUTH_MODE,
  BUYER_INFO_EXTRATION_MODE,
  CART_MODE,
  CONFIRM_SALE_MODE,
  WAITING_CONFIRMATION_MODE,
  PAYMENT_MODE,
  SUCCESSFUL_PAYMENT,
  SALE_MODE,
  type LegacyCheckoutMode,
} from './constants';
import { PaymentForm } from '@src/features/payments/legacy';
import { usePurchaseSummary } from '@src/views/checkout/hooks/usePurchaseSummary';
import { SaleDisplay, SaleConfirmation, SuccessfulPayment } from '@src/features/orders/legacy';
import { TariffExtraInfoCollect } from '@src/features/orders/form-inputs';
import { track } from '@src/services/analytics';
import { getUrlParams } from '@src/common/getUrlParams';
import { getInitialCheckoutMode } from '@src/views/checkout/utils';
import {
  resolveSaleChangeCheckoutOutcome,
  SaleChangeCheckoutOutcome,
} from '@src/domains/sale-changes/core/resolveSaleChangeCheckoutOutcome';
import FeatureFlag from '../FeatureFlag';
import { ConfirmationWait } from '@src/features/orders/awaiting-confirmation';
import { useOncePerKey } from '@src/common/hooks/useOncePerKey';

export type CheckoutProps = {
  editable?: boolean;
  skipResumeCart?: boolean;
  skipConfirmation?: boolean;
};

const callParamFunctionAsync = (param: string, order: Order, buyer: Buyer | undefined) =>
  callHostFunction(param, toSellerTemplateOrder(order), buyer);

const CheckoutLegacy = (props: CheckoutProps) => {
  const { editable = true, skipResumeCart = false, skipConfirmation = false } = props;

  const onCheckoutInit = useWidgetParam('on-checkout-init');
  const onSaleCompletion = useWidgetParam('on-sale-completion');
  const id = useWidgetId();
  const dispatch = useAppDispatch();
  const [buyer, logged] = useAppSelector(buyerSelector);
  const currentOrder = useAppSelector(currentOrderSelector);
  // Same store value the summary is built from, so it comes from the hook that already reads it.
  const { summary, saleChange: currentSaleChange } = usePurchaseSummary();
  const pendingFormInputs = useAppSelector(hasPendingSaleRequiredFormInputsSelector);
  const skipResumeCartPage = useWidgetParam('skip-resume-cart-page');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cn = classNamespaceFactory('checkout');
  const stepCheckout = useAppSelector(stepCheckoutSelector(id));
  const { steps, currentStep } = useAppSelector(checkoutStepState(id));
  const isFirstSaleCompletionFor = useOncePerKey<string>();

  const initialMode = getInitialCheckoutMode({ skipResumeCart, skipResumeCartPage });
  const [mode, setMode] = React.useState<LegacyCheckoutMode>(initialMode);

  const decideModeBasedOnSaleChange = (saleChange: SaleChange) => {
    const nextMode = resolveSaleChangeCheckoutOutcome(saleChange.status);

    if (nextMode === SaleChangeCheckoutOutcome.Payment) {
      setMode(PAYMENT_MODE);
    } else if (nextMode === SaleChangeCheckoutOutcome.OrderDetails) {
      dispatch(setShowAppliedFeedbackForSaleChange(saleChange.id));
      dispatch(resetSaleChange()); // Clear any stale sale-change so checkout uses currentOrder
      setMode(SALE_MODE);
      track('saleChangeCheckoutCompleted', { saleChangeId: saleChange.id });
    }
  };

  const decideModeBasedOnSale = (order: Order) => {
    const nextMode = resolveOrderCheckoutOutcome({
      orderStatus: order.status,
      hasPendingFormInputs: !!pendingFormInputs,
    });

    if (nextMode === OrderCheckoutOutcome.AwaitingConfirmation) {
      setMode(WAITING_CONFIRMATION_MODE);
    } else if (nextMode === OrderCheckoutOutcome.TariffExtraInfo) {
      setMode(BUYER_INFO_EXTRATION_MODE);
    } else if (nextMode === OrderCheckoutOutcome.OrderDetails) {
      // setMode(SUCCESSFUL_PAYMENT);
      setMode(SALE_MODE);

      track('checkoutCompleted', getOrderAnalyticsData(currentOrder!));
    } else {
      setMode(PAYMENT_MODE);
    }
  };

  useEffect(() => {
    const orderId = getUrlParams('orderId');

    if (orderId && buyer) {
      dispatch(setupCheckout(orderId));
    }
  }, [buyer]);

  useEffect(() => {
    dispatch(setStepCheckout({ id, value: '' }));

    switch (mode) {
      case PAYMENT_MODE:
        if (onCheckoutInit) {
          callParamFunctionAsync(onCheckoutInit, currentOrder!, buyer);
        }

        break;
      case SALE_MODE:
      case SUCCESSFUL_PAYMENT:
        // Both modes land here, and a sale change sends the checkout back into
        // SALE_MODE, so the seller page would otherwise see one purchase twice.
        if (onSaleCompletion && currentOrder && isFirstSaleCompletionFor(currentOrder.id)) {
          callParamFunctionAsync(onSaleCompletion, currentOrder, buyer);
        }

        break;
      case AUTH_MODE:
        dispatch(setStepCheckout({ id, value: LOGIN }));
        break;
      default:
    }
  }, [mode]);

  useEffect(() => {
    if (logged) {
      dispatch(loadPendingOrders());
      dispatch(loadReturnSale());
      dispatch(loadReturnOrder());
    }
  }, [logged]);

  useEffect(() => {
    if (currentOrder) {
      track('checkoutInitiated', getOrderAnalyticsData(currentOrder));
    }
  }, []);

  useEffect(() => {
    if (currentSaleChange) {
      decideModeBasedOnSaleChange(currentSaleChange);
    } else if (currentOrder) {
      decideModeBasedOnSale(currentOrder);
    }
  }, [currentSaleChange, currentOrder]);

  const onLogin = () => {
    if (currentOrder) {
      decideModeBasedOnSale(currentOrder);
    } else {
      setMode(skipConfirmation ? PAYMENT_MODE : CONFIRM_SALE_MODE);
    }
  };

  const onSaleChangeCheckout = useCallback(
    (saleChange: SaleChange) => {
      dispatch(setCurrentSaleChange(saleChange));
      dispatch(loadSaleChangeByIdAsCurrent(saleChange.id)); // fetch sale change to get the most updated data in background

      setMode(PAYMENT_MODE);
    },
    [dispatch],
  );

  const cashbackGrowthOnCheckoutSuccess =
    currentOrder != null ? (
      <FeatureFlag
        feature={CASHBACK_GROWTH_FEATURE_FLAG}
        active={() => (
          <CashbackGrowthOrchestrator
            order={currentOrder}
            renderBanner={(banner) => <CashbackGrowthSuccessBannerWrapper>{banner}</CashbackGrowthSuccessBannerWrapper>}
          />
        )}
      />
    ) : null;

  return (
    <div>
      {stepCheckout ? (
        <NavigationSteps>
          {steps.map((element, index) => {
            return (
              <StepNavigationIcon
                key={index}
                number={element}
                stepType='checkout'
                type={stepCheckout}
                isActive={currentStep?.order === element}
                mainColor={LayoutColors.justBlue}
                reverseColor={true}
              />
            );
          })}
        </NavigationSteps>
      ) : null}
      {(() => {
        switch (mode) {
          case CART_MODE:
            return <CartDisplay {...{ cn }} proceedToNextStage={() => setMode(AUTH_MODE)} editable={editable} />;
          case AUTH_MODE:
            return <AuthDisplay {...{ onLogin }} hasBackAction />;
          case CONFIRM_SALE_MODE:
            return <SaleConfirmation />;
          case WAITING_CONFIRMATION_MODE:
            return <ConfirmationWait />;
          case PAYMENT_MODE:
            return (
              <PaymentForm {...{ cn }} order={currentOrder} saleChange={currentSaleChange} purchaseSummary={summary} />
            );
          case BUYER_INFO_EXTRATION_MODE:
            return <TariffExtraInfoCollect proceedToNextStage={() => decideModeBasedOnSale(currentOrder!)} />;
          case SUCCESSFUL_PAYMENT:
            return (
              <>
                {cashbackGrowthOnCheckoutSuccess}
                <SuccessfulPayment />
              </>
            );
          case SALE_MODE:
            return (
              <>
                {cashbackGrowthOnCheckoutSuccess}
                <SaleDisplayStyleWrapper>
                  <SaleDisplay order={currentOrder!} onSaleChangeCheckout={onSaleChangeCheckout} />
                </SaleDisplayStyleWrapper>
              </>
            );
          default:
            return null;
        }
      })()}
    </div>
  );
};

const NavigationSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 26px;
`;

const CashbackGrowthSuccessBannerWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 10px;
  max-width: 800px;
`;

const SaleDisplayStyleWrapper = styled.div`
  margin: 20px auto;
  border: 1px solid #949494;
  max-width: 800px;
  padding: 10px;
  overflow: hidden;
  background-color: #fff;
  color: #444;
  border-radius: 10px;
`;

export default CheckoutLegacy;
